/**
 * RouteOptimizer Module
 * Solves Traveling Salesman Problem using Nearest Neighbor with 2-opt improvement
 * Optimized for small route sets (up to 10 stops)
 */

/**
 * Calculates Euclidean distance between two coordinates
 * @param {Object} coord1 - First coordinate {lat, lng}
 * @param {Object} coord2 - Second coordinate {lat, lng}
 * @returns {number} - Distance in degrees (for comparison purposes)
 */
function euclideanDistance(coord1, coord2) {
  const latDiff = coord1.lat - coord2.lat;
  const lngDiff = coord1.lng - coord2.lng;
  return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
}

/**
 * Calculates total route distance given a sequence and distance matrix
 * @param {number[]} sequence - Array of stop indices in order
 * @param {number[][]} distanceMatrix - Matrix of distances between stops
 * @returns {number} - Total distance
 */
function calculateRouteDistance(sequence, distanceMatrix) {
  let total = 0;
  for (let i = 0; i < sequence.length - 1; i++) {
    total += distanceMatrix[sequence[i]][sequence[i + 1]];
  }
  return total;
}

/**
 * Nearest Neighbor algorithm
 * Builds a route by always going to the nearest unvisited stop
 * @param {number[][]} distanceMatrix - Matrix of distances between stops
 * @param {number} startIndex - Index to start from (usually 0 for the starting location)
 * @returns {number[]} - Sequence of stop indices
 */
function nearestNeighbor(distanceMatrix, startIndex = 0) {
  const n = distanceMatrix.length;
  const visited = new Array(n).fill(false);
  const sequence = [startIndex];
  visited[startIndex] = true;

  let current = startIndex;

  // Visit all stops
  for (let i = 1; i < n; i++) {
    let nearest = -1;
    let minDistance = Infinity;

    // Find nearest unvisited stop
    for (let j = 0; j < n; j++) {
      if (!visited[j] && distanceMatrix[current][j] < minDistance) {
        minDistance = distanceMatrix[current][j];
        nearest = j;
      }
    }

    if (nearest === -1) break; // No more stops to visit

    sequence.push(nearest);
    visited[nearest] = true;
    current = nearest;
  }

  return sequence;
}

/**
 * 2-opt improvement algorithm
 * Iteratively improves a route by reversing segments that reduce total distance
 * @param {number[]} sequence - Initial route sequence
 * @param {number[][]} distanceMatrix - Matrix of distances between stops
 * @param {number} maxIterations - Maximum number of improvement iterations
 * @returns {number[]} - Improved sequence
 */
function twoOptImprove(sequence, distanceMatrix, maxIterations = 100) {
  let improved = true;
  let iterations = 0;
  let currentSequence = [...sequence];
  let currentDistance = calculateRouteDistance(currentSequence, distanceMatrix);

  while (improved && iterations < maxIterations) {
    improved = false;
    iterations++;

    // Try all possible pairs of edges to swap
    for (let i = 1; i < currentSequence.length - 1; i++) {
      for (let j = i + 1; j < currentSequence.length; j++) {
        // Create new sequence by reversing the segment between i and j
        const newSequence = [
          ...currentSequence.slice(0, i),
          ...currentSequence.slice(i, j + 1).reverse(),
          ...currentSequence.slice(j + 1)
        ];

        const newDistance = calculateRouteDistance(newSequence, distanceMatrix);

        // If this is better, keep it
        if (newDistance < currentDistance) {
          currentSequence = newSequence;
          currentDistance = newDistance;
          improved = true;
        }
      }
    }
  }

  return currentSequence;
}

/**
 * Creates a distance matrix from coordinates
 * Uses simple Euclidean distance for initial optimization
 * (Real distances will be calculated by DistanceCalculator module)
 * @param {Array<{lat: number, lng: number}>} coordinates - Array of coordinates
 * @returns {number[][]} - Distance matrix
 */
function createDistanceMatrix(coordinates) {
  const n = coordinates.length;
  const matrix = Array(n).fill(0).map(() => Array(n).fill(0));

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i !== j) {
        matrix[i][j] = euclideanDistance(coordinates[i], coordinates[j]);
      }
    }
  }

  return matrix;
}

/**
 * Main optimization function
 * Optimizes route using Nearest Neighbor + 2-opt improvement
 * @param {Array<{lat: number, lng: number}>} stops - Array of stop coordinates
 * @param {{lat: number, lng: number}} start - Starting location coordinate
 * @param {number[][]} [realDistanceMatrix] - Optional real distance matrix (from DistanceCalculator)
 * @returns {number[]} - Optimized sequence of stop indices (0 is starting location)
 */
export function optimize(stops, start, realDistanceMatrix = null) {
  // Validate inputs
  if (!stops || stops.length === 0) {
    throw new Error('No stops provided for optimization');
  }

  if (!start || typeof start.lat !== 'number' || typeof start.lng !== 'number') {
    throw new Error('Invalid starting location');
  }

  // Combine start location with stops (start is index 0)
  const allLocations = [start, ...stops];

  // Use provided distance matrix or create one from coordinates
  const distanceMatrix = realDistanceMatrix || createDistanceMatrix(allLocations);

  // Run Nearest Neighbor starting from index 0 (start location)
  let sequence = nearestNeighbor(distanceMatrix, 0);

  // Improve with 2-opt
  sequence = twoOptImprove(sequence, distanceMatrix);

  // Remove the starting location from the sequence (we always start there)
  // Return only the stop indices (adjusted by -1 since start was at index 0)
  return sequence.slice(1).map(idx => idx - 1);
}

/**
 * Calculates the total distance of an optimized route
 * @param {number[]} sequence - Sequence of stop indices
 * @param {number[][]} distanceMatrix - Matrix of distances
 * @returns {number} - Total distance
 */
export function getTotalDistance(sequence, distanceMatrix) {
  return calculateRouteDistance(sequence, distanceMatrix);
}
